const STORAGE_PREFIX = 'vendepay:checkout-url-params:';

const canUseWindow = () => typeof window !== 'undefined';

const storageKey = () => `${STORAGE_PREFIX}${window.location.pathname}`;

const parseParams = (search: string): Record<string, string> =>
  Object.fromEntries(new URLSearchParams(search));

const hasParams = (params: Record<string, string>) =>
  Object.keys(params).length > 0;

const normalizeParams = (params: unknown): Record<string, string> => {
  if (typeof params !== 'object' || params === null) return {};

  return Object.entries(params).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      if (typeof key === 'string' && typeof value === 'string') {
        acc[key] = value;
      }
      return acc;
    },
    {},
  );
};

const readStoredParams = (): Record<string, string> => {
  if (!canUseWindow()) return {};

  try {
    const raw = window.sessionStorage.getItem(storageKey());
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    return normalizeParams(parsed);
  } catch {
    return {};
  }
};

export const getAllUrlParams = (): Record<string, string> => {
  if (!canUseWindow()) return {};

  const current = parseParams(window.location.search);
  if (hasParams(current)) {
    try {
      window.sessionStorage.setItem(storageKey(), JSON.stringify(current));
    } catch {
      // Best effort: tracking params must not block checkout.
    }
    return current;
  }

  return readStoredParams();
};

export const getUrlParams = (param: string) => {
  const parameter = getAllUrlParams()[param];
  if (parameter) {
    return parameter;
  }
  return null;
};

export const getCurrentUrlParam = (param: string) => {
  if (!canUseWindow()) return null;

  const parameter = new URLSearchParams(window.location.search).get(param);
  if (parameter) {
    return parameter;
  }
  return null;
};
